/**
 * The How to play pages' words (docs/spec/04 §6): six pages, every line cut to the 26 columns, and
 * every figure read from the tuning so the page cannot disagree with the game. Pure.
 */

import { ENEMY_NAMES } from '../../app/brand';
import { CAPSULE_LOOK } from '../../render/sprites';
import { CAPSULE_WEIGHTS, CHALLENGE, NOVA, SCORE, TICKS_PER_SECOND } from '../../sim/tuning';
import type { CapsuleKind } from '../../sim/types';
import { clip, ordinal, tableLines, wrap } from '../kit/text';
import { TEXT_COLS } from '../kit/theme';

export type Tone = 'ink' | 'head' | 'accent' | 'muted';

export interface HelpLine {
  readonly text: string;
  readonly tone: Tone;
}

export type HelpPageId = 'controls' | 'hive' | 'capture' | 'challenge' | 'capsules' | 'nova';

export interface HelpPage {
  readonly id: HelpPageId;
  readonly title: string;
  readonly lines: readonly HelpLine[];
}

/** The most lines a page may have under its picture. */
export const MAX_LINES = 13;

type Keyed = 'left' | 'right' | 'fire' | 'bomb' | 'pause';

/** What the controls page needs to know about the input in use. */
export interface HelpInput {
  /** Both keys of each action as labels (`services.input.keyName`); '' for an empty slot. */
  readonly keys: Readonly<Record<Keyed, readonly [string, string]>>;
  /** A gamepad is connected. */
  readonly pad: boolean;
  /** Touch controls are showing. */
  readonly touch: boolean;
}

function para(text: string, tone: Tone = 'ink'): HelpLine[] {
  return wrap(text, TEXT_COLS).map((line) => ({ text: line, tone }));
}

function table(rows: readonly (readonly [string, string])[], labelW: number, tone: Tone = 'ink'): HelpLine[] {
  return tableLines(rows, labelW, TEXT_COLS).map((text) => ({ text, tone }));
}

const head = (text: string): HelpLine => ({ text, tone: 'head' });

/** `LEFT RIGHT / A D`: the first keys of each action, then the second. */
function keyPairs(pairs: readonly (readonly [string, string])[]): string {
  const sets = [0, 1].map((slot) => pairs.map((p) => p[slot] ?? '').filter((k) => k !== '').join(' '));
  return sets.filter((s) => s !== '').join(' / ') || 'NOT SET';
}

const PAD_ROWS = [['MOVE', 'D-PAD OR LEFT STICK'], ['FIRE', 'A'], ['NOVA', 'B OR X'], ['PAUSE', 'START']] as const;
const TOUCH_ROWS = [
  ['MOVE', 'SLIDE, BOTTOM LEFT'], ['FIRE', 'FIRE, BOTTOM RIGHT'], ['NOVA', 'NOVA, ABOVE FIRE'], ['PAUSE', 'TOP RIGHT'],
] as const;

function controlsPage(input: HelpInput): HelpPage {
  const lines: HelpLine[] = [];
  if (input.touch) lines.push(head('TOUCH'), ...table(TOUCH_ROWS, 6));
  else {
    const k = input.keys;
    const rows = [
      ['MOVE', keyPairs([k.left, k.right])], ['FIRE', keyPairs([k.fire])],
      ['NOVA', keyPairs([k.bomb])], ['PAUSE', keyPairs([k.pause])],
    ] as const;
    lines.push(head('KEYBOARD'), ...table(rows, 6));
  }
  if (input.pad) lines.push(head('GAMEPAD'), ...table(PAD_ROWS, 6));
  lines.push({ text: 'THE NOVA IS FOR SUPERNOVA.', tone: 'muted' });
  if (!input.touch) lines.push({ text: 'REBIND THE KEYS IN OPTIONS', tone: 'muted' });
  return { id: 'controls', title: 'CONTROLS', lines: lines.slice(0, MAX_LINES) };
}

export interface HiveRow {
  readonly sprite: 'stinger' | 'moth' | 'monarch';
  readonly name: string;
  readonly home: number;
  readonly diving: number;
}

/** The hive's three and what each pays at home and diving (02 §8). */
export function hiveRows(): HiveRow[] {
  return [
    { sprite: 'stinger', name: ENEMY_NAMES.stinger, home: SCORE.stinger[0], diving: SCORE.stinger[1] },
    { sprite: 'moth', name: ENEMY_NAMES.moth, home: SCORE.moth[0], diving: SCORE.moth[1] },
    { sprite: 'monarch', name: ENEMY_NAMES.monarch, home: SCORE.monarch[0], diving: SCORE.monarch[1] },
  ];
}

function hiveLine(name: string, home: string, diving: string): string {
  return clip(name, 8).padEnd(9) + home.padStart(5) + diving.padStart(8);
}

function hivePage(): HelpPage {
  const [, , oneEscort, twoEscorts] = SCORE.monarch;
  const trio = `${SCORE.morph} EACH, ${SCORE.morphBonus[0]} UP FOR ALL`;
  return {
    id: 'hive', title: 'THE HIVE',
    lines: [
      { text: hiveLine('', 'HOME', 'DIVING'), tone: 'muted' },
      ...hiveRows().map((r): HelpLine => ({ text: hiveLine(r.name, String(r.home), String(r.diving)), tone: 'ink' })),
      head(`THE ${ENEMY_NAMES.monarch}`),
      ...para('TAKES TWO HITS. THE FIRST TURNS IT VIOLET.'),
      ...para(`DIVING WITH ONE ESCORT IT PAYS ${oneEscort}, WITH TWO ${twoEscorts}.`),
      ...para(`A ${ENEMY_NAMES.stinger} MAY SPLIT IN THREE: ${trio}.`, 'muted'),
    ],
  };
}

function capturePage(): HelpPage {
  const m = ENEMY_NAMES.monarch;
  return {
    id: 'capture', title: 'CAPTURE AND RESCUE',
    lines: [
      ...table([
        ['1', 'LET A BEAM TAKE A SHIP.'],
        ['2', `SHOOT THAT ${m} WHILE IT DIVES.`],
        ['3', 'YOUR SHIP FLIES BACK TO YOUR SIDE: THE DUAL FIGHTER.'],
      ], 2),
      ...para('TWICE THE GUNS, TWICE THE TARGET.', 'accent'),
      ...para(`SHOOT THE ${m} AT HOME AND THE CAPTIVE IS LOST.`, 'muted'),
    ],
  };
}

function challengePage(): HelpPage {
  const bonus = CHALLENGE.waveBonus;
  return {
    id: 'challenge', title: 'CHALLENGING STAGES',
    lines: [
      ...para('EVERY FOURTH STAGE THE HIVE FLIES THROUGH AND NEVER FIRES.'),
      ...para(`EACH HIT PAYS ${CHALLENGE.hit}.`, 'accent'),
      ...para(`ALL EIGHT OF A WAVE: ${bonus[0]}, RISING TO ${bonus[bonus.length - 1] ?? bonus[0]}.`),
      ...para(`ALL FORTY: PERFECT! ${CHALLENGE.perfect}.`, 'accent'),
    ],
  };
}

const seconds = (ticks: number): number => Math.round(ticks / TICKS_PER_SECOND);

/** What each capsule does, in a few words (03 §2). */
const CAPSULE_TEXT: Readonly<Record<CapsuleKind, string>> = {
  rapid: 'HOLD TO AUTO-FIRE',
  spread: 'THREE-WAY SHOTS',
  pierce: 'SHOTS GO THROUGH',
  shield: 'ABSORBS ONE HIT',
  chrono: 'SLOWS THE HIVE',
  nova: `A BOMB, UP TO ${NOVA.maxNovas}`,
  life: 'A SHIP IN RESERVE',
};

/** Characters beside a capsule's picture. */
export const CAPSULE_COLS = 23;

export interface CapsuleRow {
  readonly kind: CapsuleKind;
  readonly name: string;
  readonly text: string;
}

/** One line a capsule, in the order the game weighs them. */
export function capsuleRows(): CapsuleRow[] {
  return CAPSULE_WEIGHTS.map(([kind]) => {
    const name = CAPSULE_LOOK[kind].name;
    return { kind, name, text: clip(CAPSULE_TEXT[kind], CAPSULE_COLS - name.length - 1) };
  });
}

function capsulesPage(): HelpPage {
  const look = CAPSULE_LOOK;
  const timed = `${look.rapid.name}, ${look.spread.name} AND ${look.pierce.name}`;
  return {
    id: 'capsules', title: 'CAPSULES',
    lines: [
      ...para(`${timed} LAST ${seconds(NOVA.timed)} SECONDS, ${look.chrono.name} ${seconds(NOVA.chronoTicks)}.`),
      ...para('KILLS EARN THEM. A DIVER COUNTS TWICE.', 'muted'),
      ...para('ALL BUT YOUR BOMBS ARE LOST WITH THE SHIP.', 'muted'),
    ],
  };
}

/** `X2 AT 5`: the chain length each multiplier starts at (03 §4). */
export function chainSteps(): string[] {
  return NOVA.chainTiers.map((at, i) => `X${i + 2} AT ${at}`);
}

function novaPage(): HelpPage {
  const steps = chainSteps();
  const pairs: string[] = [];
  for (let i = 0; i < steps.length; i += 2) pairs.push(steps.slice(i, i + 2).join(' · '));
  const when = `EVERY ${ordinal(NOVA.empressEvery)} STAGE`;
  return {
    id: 'nova', title: 'CHAIN, NOVA, EMPRESS',
    lines: [
      head('THE CHAIN'),
      ...para('QUICK KILLS MULTIPLY YOUR POINTS:'),
      ...pairs.flatMap((p) => para(p, 'accent')),
      head('THE NOVA BOMB'),
      ...para(`CLEARS EVERY BOMB AND HITS ALL THAT FLIES. YOU START WITH ${NOVA.startNovas}.`),
      head(`THE ${ENEMY_NAMES.empress}`),
      ...para(`WAITS ON ${when}. KILL HER FOR ${NOVA.empressPoints} AND TWO CAPSULES.`),
    ],
  };
}

/** The six pages, the first written for the input in use. */
export function helpPages(input: HelpInput): HelpPage[] {
  return [controlsPage(input), hivePage(), capturePage(), challengePage(), capsulesPage(), novaPage()];
}
